import { Clock, MapPinned } from "lucide-react";
import AddToCalendar from "./AddToCalendar";
import EventLink from "./EventLink";
import ShareButton from "./ShareButton";
import VerifiedEventCheck from "./VerifiedEventCheck";
import ProfileFooter from "./ProfileFooter";
import { Event, FormattedDate, ProfileData } from "@/types/api";

interface StandaloneEventPageProps {
    event: Event;
    formattedStartDate?: FormattedDate;
    formattedStartTime?: string;
    formattedEndTime?: string;
    creatorProfileData: ProfileData | null;

}
const StandaloneEventPage = ({ event, formattedStartDate, formattedStartTime, formattedEndTime, creatorProfileData }: StandaloneEventPageProps) => {
    const creatorName = creatorProfileData?.display_name ?? creatorProfileData?.username;

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-500 to-indigo-600 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                    <div className="p-4 bg-indigo-600 text-white">
                        <h1 className="text-2xl md:text-3xl font-bold mb-4">
                            {event.title}
                            {event.is_verified && <VerifiedEventCheck className="inline-flex align-baseline ml-2" />}
                        </h1>

                        <div className="flex flex-row gap-3">
                            <div className="flex flex-wrap gap-3 items-center">
                                <AddToCalendar event={event} showExpandedCTA={true} />
                                <ShareButton event={event} showExpandedCTA={true} />
                                {event.event_url && (
                                    <EventLink url={event.event_url} />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8">
                        <div className="flex flex-wrap gap-4 mb-6">
                            <div className="bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-xl px-4 py-2 text-white shadow-lg shadow-fuchsia-200">
                                <div className="text-2xl font-bold flex items-center justify-center gap-1.5">
                                    <span className="text-fuchsia-100">{formattedStartDate?.month}</span>
                                    <span>{formattedStartDate?.day}</span>
                                </div>
                                <div className="h-px bg-white/20 my-1.5 w-full"></div>
                                <div className="text-sm text-fuchsia-100">
                                    {formattedStartDate?.weekday}
                                </div>
                            </div>
                            <div className="flex items-center bg-indigo-50 text-indigo-800 rounded-lg px-4 py-2">
                                <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                                <span>
                                    {formattedStartTime}
                                    {formattedEndTime && ` - ${formattedEndTime}`}
                                </span>
                            </div>
                            <div className="flex items-center bg-indigo-50 text-indigo-800 rounded-lg px-4 py-2">
                                <MapPinned className="h-5 w-5 mr-2 text-indigo-500" />
                                <span>{event.venue}</span>
                            </div>
                        </div>
                        {event.description && (
                            <div className="mt-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-3">About this event</h2>
                                <div className="prose prose-indigo max-w-none text-gray-700">
                                    <p>{event.description}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {creatorProfileData && creatorName && (
                    <div className="flex items-center justify-center text-center mb-8">
                        {creatorProfileData.avatar_url && (
                            <img
                                src={creatorProfileData.avatar_url}
                                alt={`${creatorName}'s avatar`}
                                className="w-8 h-8 rounded-full mr-3 object-cover border border-indigo-200 shadow-sm"
                            />
                        )}
                        <a
                            href={`https://allmygigs.com/${creatorProfileData.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-100 hover:text-white hover:underline transition-colors duration-200"
                        >
                            See all of {creatorName}'s upcoming gigs on{' '}
                            <span className="font-semibold text-indigo-50 hover:text-white">
                                allmygigs
                            </span>.
                        </a>
                    </div>
                )}
            </div>
            <ProfileFooter username={creatorName} />
        </div>
    )
}
export default StandaloneEventPage;